/**
 * Динамический favicon под цвет бандла — точно как карточки на
 * admin-hub лендинге (dashboard.<домен>): градиент per-секция + белая
 * SVG-иконка той же формы.
 *
 *   sales / dashboard / главный → зелёный градиент + диаграмма
 *   production                  → красно-кирпичный + здание
 *   finance                     → оранжевый + знак $
 *   warehouse                   → коричневый + куб
 *   messenger                   → синий + чат-пузырь
 *
 * SVG inline через data-URL, без новых файлов.
 */
type BrandKind = "sales" | "production" | "finance" | "warehouse" | "messenger" | "marketing" | "commercial" | "auto";

interface BrandSpec {
  /** Градиент: from → to. */
  from: string;
  to: string;
  /** Тело иконки внутри 64×64 — белая обводка, stroke 4. */
  iconPath: string;
  /** Опциональные доп. атрибуты SVG <g> вокруг path. */
  iconExtra?: string;
  /** Название раздела (для splash-экранов и тайтлов). */
  title: string;
  /** Подзаголовок (для splash) — короткое описание. */
  subtitle: string;
}

export const BRAND: Record<Exclude<BrandKind, "auto">, BrandSpec> = {
  sales: {
    from: "#4fae4e", to: "#388e3c",
    iconPath: "M21 16V48 M21 32L44 16 M21 32L44 48",
    title: "Отдел продаж",
    subtitle: "Сводка, менеджеры, звонки, отчёты, выезды",
  },
  production: {
    from: "#ef5350", to: "#c62828",
    iconPath: "M14 54h36 M18 54V22l14-8 14 8v32 M26 30h12 M26 38h12 M26 46h12",
    title: "Производство",
    subtitle: "Календарь проектов, AmoCRM-связки, ДДС объектов",
  },
  finance: {
    from: "#ffb74d", to: "#f57c00",
    iconPath: "M32 8v48 M42 16h-13a5 5 0 000 14h10a5 5 0 010 14h-14",
    title: "Финансы",
    subtitle: "P&L, движение ДС, ФОТ, сотрудники",
  },
  warehouse: {
    from: "#8d6e63", to: "#5d4037",
    iconPath:
      "M50 42V22a3 3 0 00-1.5-2.6L34 11a3 3 0 00-4 0L15.5 19.4A3 3 0 0014 22v20a3 3 0 001.5 2.6L30 53a3 3 0 004 0l14.5-8.4A3 3 0 0050 42z " +
      "M14.5 19.9L32 30.0 49.5 19.9 M32 53V30",
    title: "Склад",
    subtitle: "Учёт материалов и остатков",
  },
  messenger: {
    from: "#42a5f5", to: "#1976d2",
    iconPath:
      "M54 31.5a18.4 18.4 0 01-2 8.3A18.5 18.5 0 0135.4 50a18.4 18.4 0 01-8.3-2L14 52l4-13.1a18.4 18.4 0 01-2-8.3 18.5 18.5 0 0110.3-16.6A18.4 18.4 0 0134.6 12h1.1A18.5 18.5 0 0154 30.4z",
    title: "Мессенджер",
    subtitle: "Командный чат, бот зарплаты, диск",
  },
  marketing: {
    // Фиолетово-голубая палитра (выделяет от sales/production/finance) — близко
    // к BI-разделам и аналитическим продуктам.
    from: "#7e57c2", to: "#4527a0",
    // Иконка «мишень»: концентрические круги + точка (цели/перформанс).
    iconPath: "M32 32",
    iconExtra: "<circle cx='32' cy='32' r='18'/><circle cx='32' cy='32' r='10'/><circle cx='32' cy='32' r='4' fill='white' stroke='none'/>",
    title: "Маркетинг",
    subtitle: "Я.Директ, кампании, расход и эффективность",
  },
  commercial: {
    // Бирюзово-зелёная палитра — коммерция/юнит-экономика.
    from: "#14b8a6", to: "#0f766e",
    // Иконка «портфель»: коммерция/бизнес.
    iconPath: "M10 22h44v28a3 3 0 0 1-3 3H13a3 3 0 0 1-3-3z M23 22v-5a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v5",
    iconExtra: "<path d='M10 33h44'/>",
    title: "Коммерческий",
    subtitle: "Маркетинг + ОП + финансы, юнит-экономика",
  },
};


export type BrandKey = Exclude<BrandKind, "auto">;

export function resolveBrand(kind: BrandKind = "auto"): BrandKey {
  if (kind !== "auto") return kind;
  return detectKindByHost() ?? "sales";
}


function detectKindByHost(): Exclude<BrandKind, "auto"> | null {
  if (typeof window === "undefined") return null;
  const h = window.location.hostname;
  if (h.startsWith("finance."))    return "finance";
  if (h.startsWith("production.")) return "production";
  if (h.startsWith("warehouse."))  return "warehouse";
  if (h.startsWith("messenger."))  return "messenger";
  if (h.startsWith("marketing."))  return "marketing";
  if (h.startsWith("commercial.")) return "commercial";
  // VITE_APP_MODE=chat фолбэк для случая когда hostname не messenger.*
  if ((import.meta.env.VITE_APP_MODE as string | undefined) === "chat") return "messenger";
  return "sales";
}


export function setBrandFavicon(kind: BrandKind = "auto"): void {
  if (typeof document === "undefined") return;
  const resolved = kind === "auto" ? detectKindByHost() : kind;
  if (!resolved) return;
  const spec = BRAND[resolved];
  if (!spec) return;

  const gradId = `g_${resolved}`;
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'>` +
    `<defs>` +
    `<linearGradient id='${gradId}' x1='0' y1='0' x2='1' y2='1'>` +
    `<stop offset='0%' stop-color='${spec.from}'/>` +
    `<stop offset='100%' stop-color='${spec.to}'/>` +
    `</linearGradient>` +
    `</defs>` +
    `<rect width='64' height='64' rx='14' fill='url(#${gradId})'/>` +
    `<g fill='none' stroke='white' stroke-width='4' stroke-linecap='round' stroke-linejoin='round'>` +
    `<path d='${spec.iconPath}'/>` +
    `${spec.iconExtra ?? ""}` +
    `</g>` +
    `</svg>`;
  const href = `data:image/svg+xml,${encodeURIComponent(svg)}`;

  document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]').forEach(el => el.remove());
  const link = document.createElement("link");
  link.rel = "icon";
  link.type = "image/svg+xml";
  link.href = href;
  document.head.appendChild(link);
}
